import React from 'react';
import { Box, ButtonBase, IconButton, InputBase, Tooltip } from '@mui/material';
import { AccountTreeOutlined, Add, Close, FilterListOutlined, KeyboardArrowDown, Search, SettingsOutlined, ViewKanbanOutlined, ViewListOutlined } from '@mui/icons-material';
import { ActionPicker } from 'ui-components/SelectionActionBar';
import { useDealFlowView } from 'context/DealFlowViewContext';

// Compact controls that live in the grouped list's sticky status rail (pipeline on the left; search, filter and
// view on the right) - the HubSpot / Pipedrive pattern: the rarely changed pipeline in a dropdown, the statuses
// in front. Same 32px height and hairline style as the rail's other controls.
const controlSx = {
    flexShrink: 0, height: 32, px: '10px', gap: '6px', borderRadius: '8px',
    fontSize: '0.8125rem', fontWeight: 600, color: '#344054', border: '1px solid #EAECF0', bgcolor: '#FFFFFF',
    '& svg': { fontSize: 18 },
    '&:hover': { bgcolor: '#F9FAFB' },
    '&.Mui-focusVisible': { outline: '2px solid #3538CD', outlineOffset: '1px' },
};

// Pipeline dropdown: the status picker (search, list, Add / Manage footer) with "All pipelines" kept.
export const PipelineSwitcher = ({ pipelines = [], value, onChange, onAddPipeline, onManagePipelines, canAddPipeline = true }) => {
    const [anchor, setAnchor] = React.useState(null);
    const current = pipelines.find((p) => p?.value === value);
    const label = !current || current.value === 'All' ? 'All pipelines' : current.label;
    return (
        <>
            <Tooltip title="Pipeline">
                <ButtonBase
                    onClick={(e) => setAnchor(e.currentTarget)}
                    aria-haspopup="listbox"
                    aria-expanded={Boolean(anchor)}
                    aria-label={`Pipeline: ${label}. Change pipeline`}
                    sx={{ ...controlSx, maxWidth: { xs: 150, sm: 220 }, ...(anchor && { bgcolor: '#F2F4F7' }) }}
                >
                    <AccountTreeOutlined sx={{ color: '#444CE7' }} />
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>
                    <KeyboardArrowDown sx={{ color: '#667085', transition: 'transform 0.15s ease', transform: anchor ? 'rotate(180deg)' : 'none' }} />
                </ButtonBase>
            </Tooltip>
            {anchor && (
                <ActionPicker
                    anchorEl={anchor}
                    placement="bottom-start"
                    onClose={() => setAnchor(null)}
                    menu={{
                        title: 'Pipelines',
                        searchPlaceholder: 'Search pipelines...',
                        includeAll: true,
                        optionIcon: <AccountTreeOutlined />,
                        options: pipelines.map((p) => ({
                            ...p,
                            label: p.value === 'All' ? 'All pipelines' : p.label,
                            selected: p.value === value,
                        })),
                        onSelect: (option) => option.value !== value && onChange(option.value),
                        footer: [
                            canAddPipeline && onAddPipeline && { key: 'add', label: 'Add Pipeline', icon: <Add />, onClick: onAddPipeline },
                            onManagePipelines && { key: 'manage', label: 'Manage Pipelines', icon: <SettingsOutlined />, onClick: onManagePipelines },
                        ],
                    }}
                />
            )}
        </>
    );
};

// Search as an icon that opens into a field; it stays open while it holds text. Enter searches, × clears.
export const CompactSearch = ({ value = '', onChange, onSubmit, onClear }) => {
    const [open, setOpen] = React.useState(Boolean(value));
    const inputRef = React.useRef(null);
    const expanded = open || Boolean(value);
    React.useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

    if (!expanded) {
        return (
            <Tooltip title="Search">
                <IconButton onClick={() => setOpen(true)} aria-label="Search startups" sx={{ ...controlSx, width: 32, px: 0 }}>
                    <Search />
                </IconButton>
            </Tooltip>
        );
    }
    return (
        <Box sx={{
            ...controlSx, display: 'flex', alignItems: 'center', width: { xs: 170, sm: 220 }, cursor: 'text', fontWeight: 400,
            '&:focus-within': { borderColor: '#A4BCFD', boxShadow: '0 0 0 3px rgba(68,76,231,0.12)' },
        }}>
            <Search sx={{ color: '#98A2B3' }} />
            <InputBase
                inputRef={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onSubmit();
                    if (e.key === 'Escape') { if (value) onClear(); else setOpen(false); }
                }}
                onBlur={() => { if (!value) setOpen(false); }}
                placeholder="Search by name"
                inputProps={{ 'aria-label': 'Search by name' }}
                sx={{ flex: 1, fontSize: '0.8125rem' }}
            />
            {value && (
                <IconButton size="small" onClick={onClear} aria-label="Clear search" sx={{ p: '2px', color: '#98A2B3' }}>
                    <Close sx={{ fontSize: 16 }} />
                </IconButton>
            )}
        </Box>
    );
};

// Filter button that says how many filters are on.
export const FilterButton = ({ activeCount = 0, onClick }) => (
    <ButtonBase
        onClick={onClick}
        aria-label={activeCount ? `Filter, ${activeCount} active` : 'Filter'}
        sx={{ ...controlSx, ...(activeCount > 0 && { color: '#3538CD', borderColor: '#C7D7FE', bgcolor: '#EEF4FF', '&:hover': { bgcolor: '#E0EAFF' } }) }}
    >
        <FilterListOutlined />
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Filter</Box>
        {activeCount > 0 && (
            <Box component="span" sx={{ minWidth: 18, height: 18, px: '5px', borderRadius: '9px', bgcolor: '#444CE7', color: '#FFFFFF', fontSize: '0.6875rem', lineHeight: '18px', textAlign: 'center' }}>
                {activeCount}
            </Box>
        )}
    </ButtonBase>
);

// List / Board switch (same shell state as the old icon toggle); hidden on phones, where Deal Flow is list-only.
export const ViewSwitch = () => {
    const ctx = useDealFlowView();
    if (!ctx) return null;
    const { view, setView } = ctx;
    const item = (key, label, Icon) => {
        const active = (key === 'list' ? view === 'list' : view !== 'list');
        return (
            <ButtonBase
                key={key}
                onClick={() => !active && setView(key)}
                aria-pressed={active}
                sx={{
                    height: 26, px: '8px', gap: '5px', borderRadius: '6px', fontSize: '0.8125rem', fontWeight: 600,
                    color: active ? '#101828' : '#667085', bgcolor: active ? '#FFFFFF' : 'transparent',
                    boxShadow: active ? '0 1px 2px rgba(16,24,40,0.10)' : 'none',
                    '& svg': { fontSize: 17 },
                    '&:hover': { color: '#101828' },
                    '&.Mui-focusVisible': { outline: '2px solid #3538CD', outlineOffset: '1px' },
                }}
            >
                <Icon /> {label}
            </ButtonBase>
        );
    };
    return (
        <Box role="group" aria-label="View" sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0, gap: '2px', p: '2px', borderRadius: '8px', bgcolor: '#F2F4F7', border: '1px solid #EAECF0' }}>
            {item('list', 'List', ViewListOutlined)}
            {item('cards', 'Board', ViewKanbanOutlined)}
        </Box>
    );
};
